// Core imports
import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Component imports
import { Modal, Button, Typography, Space, Row, Col, Divider } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import moment from 'moment-timezone';
import { DEFAULT_DATETIME_TIMEZONE_FORMAT } from '../../constants/main';
import { useParams } from 'react-router-dom';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { useFetchOrganizationQuery } from '../../store/organization/organizationApi';

// Constant declarations
const { Text } = Typography;
const ConflictChangesModal = ({ isOpen, onCancel, onOverwrite, conflictInfo = {} }) => {
  const { orgId, tenantId } = useParams();
  const { t } = useTranslation();
  const { data: bot } = getCurrentQueryState('fetchBot', { orgId, tenantId });
  const { data: organization } = useFetchOrganizationQuery({ orgId }, { skip: !!tenantId });
  const { lastModifiedBy, updatedAt: lastModifiedAt } = conflictInfo;

  return (
    <Modal
      open={isOpen}
      onCancel={onCancel}
      closable={true}
      maskClosable={false}
      className="ant-modal-p-0"
      centered
      width={550}
      footer={null}
    >
      <Row gutter={[16, 16]}>
        <Col span={24} className="px-8 py-3">
          <Space align="start" size="middle">
            <Text className="text-warning fs-xl" >
              <WarningOutlined className="mr-2"/>
              {t('Conflicting Changes Detected')}
            </Text>
          </Space>
        </Col>

        <Col span={24} className="px-8">
          <Text>
            {t('Another user has made changes to this flow from a different device while you were editing. If you continue, your changes will overwrite theirs.')}
          </Text>
        </Col>

        {lastModifiedBy && (
          <Col span={24} className="d-flex justify-content-center px-8">
            <Row className="w-80">
              <Col xs={24} className="pb-3">
                <Text strong className="mr-1">{t('User')}:</Text>
                <Text>
                  {lastModifiedBy.name} - <Typography.Link href={`mailto:${lastModifiedBy.email}`}>{lastModifiedBy.email}</Typography.Link>
                </Text>
              </Col>
              <Col xs={24}>
                <Text strong>{t('Last edit')}:</Text> <Text>{lastModifiedAt ? moment(lastModifiedAt).tz(bot?.timezone || organization?.timezone || 'UTC').format(DEFAULT_DATETIME_TIMEZONE_FORMAT) : 'Recently'}</Text>
              </Col>
            </Row>
          </Col>
        )}

        <Col span={24}>
          <Divider className="mb-0"/>

          <Row className="px-4 pb-3">
            <Col xs={24} className="text-right">
              <Text className="text-warning fs-sm pr-14">
                <WarningOutlined className="pr-1" />
                {t('Non Reversible Action!')}
              </Text>
            </Col>

            <Col xs={24}>
              <Row gutter={8} wrap={false} align="middle" className="w-100">
                <Col xs={12}>
                  <Button
                    size="large"
                    className="w-100"
                    type="default"
                    onClick={onCancel}
                  >
                    {t('Cancel Saving')}
                  </Button>
                </Col>
                <Col xs={12}>
                  <Button
                    size="large"
                    className="w-100"
                    type="primary"
                    onClick={onOverwrite}
                  >
                    {t('Overwrite their Changes')}
                  </Button>
                </Col>
              </Row>

            </Col>
          </Row>
        </Col>
      </Row>
    </Modal>
  );
};

ConflictChangesModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onOverwrite: PropTypes.func.isRequired,
  conflictInfo: PropTypes.shape({
    lastModifiedBy: PropTypes.shape({
      name: PropTypes.string,
      email: PropTypes.string
    }),
    updatedAt: PropTypes.number
  })
};

export default ConflictChangesModal;
